import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import API, { UPLOADS_URL } from '../api/axios';
import SEO from '../components/SEO';
import { fixBlogContent } from '../utils/contentImages';

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [recent, setRecent] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagsFetched, setTagsFetched] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchBlog = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await API.get(`/blogs/public/${slug}`);
        setBlog(res.data.data);
      } catch (err) {
        setError(true);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);

  useEffect(() => {
    if (!blog) return;
    API.get(`/blogs/related/${blog.id}`).then(r => setRelated(r.data.data || [])).catch(() => {});
  }, [blog]);

  useEffect(() => {
    API.get('/blogs/latest').then(r => setRecent(r.data.data || [])).catch(() => {});
    API.get('/blogs/categories/public').then(r => setCategories(r.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    API.get('/blogs/tags/public').then(r => { setTags(r.data.data || []); setTagsFetched(true); }).catch(() => setTagsFetched(true));
  }, []);

  const shareUrl = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(blog?.title || '');
    switch (platform) {
      case 'facebook': return `https://facebook.com/sharer/sharer.php?u=${url}`;
      case 'twitter': return `https://twitter.com/intent/tweet?text=${title}&url=${url}`;
      case 'whatsapp': return `https://wa.me/?text=${title}%20${url}`;
      case 'linkedin': return `https://linkedin.com/sharing/share-offsite/?url=${url}`;
      default: return '#';
    }
  };

  const readTime = (content) => {
    if (!content) return '1 min read';
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length;
    const mins = Math.max(1, Math.ceil(words / 200));
    return `${mins} min read`;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getInitials = (name) => {
    if (!name) return 'A';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="loading-screen flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-secondary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted">Loading post...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="error-message flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-50 flex items-center justify-center">
            <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <h2 className="text-2xl font-bold text-primary mb-2">Blog Not Found</h2>
          <p className="text-body mb-6">The blog post you're looking for doesn't exist or has been removed.</p>
          <Link to="/blog" className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-full hover:bg-primary-dark transition-colors shadow-md">
            <i className="fas fa-arrow-left text-sm"></i>
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const siteUrl = (process.env.REACT_APP_SITE_URL || 'https://sawsindirapuram.com').replace(/\/+$/, '');
  const heroImage = blog.image ? `${UPLOADS_URL}/${blog.image}` : null;
  const author = blog.author || 'Admin';
  const category = blog.category || null;
  const clean = (t) => (t ? String(t).replace(/<[^>]*>/g, '').trim() : '');
  const blogTags = blog.tags
    ? (Array.isArray(blog.tags) ? blog.tags : String(blog.tags).split(',').map((t) => t.trim()).filter(Boolean))
    : [];
  const blogUrl = `${siteUrl}/blog/${blog.slug}`;
  const blogExcerpt = blog.content ? clean(blog.content).substring(0, 160) : '';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: clean(blog.title),
    description: blogExcerpt,
    image: heroImage || `${siteUrl}/og-image.jpg`,
    author: { '@type': 'Person', name: author },
    publisher: {
      '@type': 'Organization',
      name: 'St. Andrews World School',
      logo: { '@type': 'ImageObject', url: `${siteUrl}/logo192.png` },
    },
    datePublished: blog.created_at || blog.createdAt,
    dateModified: blog.updated_at || blog.updatedAt || blog.created_at || blog.createdAt,
    mainEntityOfPage: { '@type': 'WebPage', '@id': blogUrl },
  };

  return (
    <>
      <SEO
        title={clean(blog.title)}
        description={blogExcerpt}
        ogImage={heroImage}
        canonical={blogUrl}
        keywords={blogTags.join(', ')}
        schema={articleSchema}
        article={{
          publishedTime: blog.created_at || blog.createdAt,
          modifiedTime: blog.updated_at || blog.updatedAt || blog.created_at || blog.createdAt,
          author,
          tags: blogTags,
        }}
      />

      <article>
        <section className="py-12 md:py-16 bg-light">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-14" style={{ opacity: 0, animation: 'fadeIn 0.6s ease forwards' }}>

              {/* MAIN */}
              <div className="flex-1 lg:w-2/3 min-w-0">

                {/* Image */}
                {heroImage && (
                  <div className="rounded-2xl overflow-hidden shadow-lg mb-8 bg-gray-100">
                    <img src={heroImage} alt={clean(blog.title)} className="w-full h-auto object-contain bg-gray-100" />
                  </div>
                )}

                {category && (
                  <span className="inline-block bg-secondary text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider shadow-md mb-4">
                    {category}
                  </span>
                )}

                <h1 className="text-[1.5rem] font-bold text-primary leading-tight mb-4">
                  {clean(blog.title)}
                </h1>

                <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-10 text-muted text-sm font-medium">
                  <span className="flex items-center gap-2">
                    <i className="fas fa-user text-secondary"></i>
                    {author}
                  </span>
                  <span className="flex items-center gap-2">
                    <i className="far fa-calendar text-secondary"></i>
                    {formatDate(blog.created_at)}
                  </span>
                  <span className="flex items-center gap-2">
                    <i className="far fa-clock text-secondary"></i>
                    {readTime(blog.content)}
                  </span>
                </div>

                <div className="bg-white rounded-2xl p-6 md:p-10 shadow-sm border border-gray-100">
                  <div
                    className="blog-content text-body leading-relaxed text-lg space-y-6"
                    dangerouslySetInnerHTML={{ __html: fixBlogContent(blog.content) }}
                  />
                </div>

                <hr className="my-12 border-secondary/30" />

                {/* SHARE */}
                <div className="mb-12">
                  <p className="text-sm font-semibold uppercase tracking-wider text-muted mb-4">Share this post:</p>
                  <div className="flex items-center gap-3">
                    <a href={shareUrl('facebook')} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-all duration-300 shadow-sm hover:shadow-md">
                      <i className="fab fa-facebook-f"></i>
                    </a>
                    <a href={shareUrl('twitter')} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-black hover:text-white hover:border-black transition-all duration-300 shadow-sm hover:shadow-md">
                      <i className="fab fa-x-twitter"></i>
                    </a>
                    <a href={shareUrl('whatsapp')} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-all duration-300 shadow-sm hover:shadow-md">
                      <i className="fab fa-whatsapp"></i>
                    </a>
                    <a href={shareUrl('linkedin')} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-all duration-300 shadow-sm hover:shadow-md">
                      <i className="fab fa-linkedin-in"></i>
                    </a>
                  </div>
                </div>

                {/* AUTHOR */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row items-start gap-6 mb-12 shadow-sm">
                  <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center flex-shrink-0 shadow-md">
                    <span className="text-white font-bold text-lg">{getInitials(author)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted mb-1">About the Author</p>
                    <h4 className="text-lg font-bold text-primary mb-2">{author}</h4>
                    <p className="text-body text-base leading-relaxed">
                      {author} is a contributor at our platform, sharing insights and knowledge with our readers. Stay tuned for more articles from {author}.
                    </p>
                  </div>
                </div>
              </div>

              {/* SIDEBAR */}
              <aside className="lg:w-1/3 lg:max-w-sm w-full" style={{ opacity: 0, animation: 'fadeIn 0.6s ease 0.2s forwards' }}>
                <div className="space-y-8 lg:sticky lg:top-24">

                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-5 flex items-center gap-2">
                      <span className="w-1 h-5 bg-secondary rounded-full inline-block"></span>
                      Recent Posts
                    </h3>
                    <div className="space-y-4">
                      {recent.slice(0, 5).map((post) => (
                        <Link key={post.id} to={`/blog/${post.slug}`} className="flex items-start gap-3 group">
                          <div className="w-14 h-14 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                            {post.image ? (
                              <img src={`${UPLOADS_URL}/${post.image}`} alt={clean(post.title)} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-muted">
                                <i className="fas fa-file-alt"></i>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-primary leading-snug group-hover:text-secondary transition-colors line-clamp-2">{clean(post.title)}</h4>
                            <p className="text-xs text-muted mt-1">{formatDate(post.created_at)}</p>
                          </div>
                        </Link>
                      ))}
                      {recent.length === 0 && <p className="text-sm text-muted">No recent posts.</p>}
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-5 flex items-center gap-2">
                      <span className="w-1 h-5 bg-secondary rounded-full inline-block"></span>
                      Categories
                    </h3>
                    <div className="space-y-2">
                      {categories.map((cat) => (
                        <Link key={cat.id || cat.name} to={`/blog/category/${cat.slug || cat.name?.toLowerCase().replace(/\s+/g, '-')}`}
                          className="flex items-center justify-between px-4 py-2.5 rounded-lg hover:bg-light transition-colors group">
                          <span className="text-sm text-body group-hover:text-primary transition-colors">{cat.name}</span>
                          {cat.post_count !== undefined && (
                            <span className="text-xs bg-gray-100 text-muted px-2 py-0.5 rounded-full group-hover:bg-primary group-hover:text-white transition-colors">{cat.post_count}</span>
                          )}
                        </Link>
                      ))}
                      {categories.length === 0 && <p className="text-sm text-muted">No categories found.</p>}
                    </div>
                  </div>

                  {tagsFetched && tags.length > 0 && (
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <h3 className="text-sm font-bold uppercase tracking-wider text-primary mb-5 flex items-center gap-2">
                        <span className="w-1 h-5 bg-secondary rounded-full inline-block"></span>
                        Tags
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {tags.map((tag) => (
                          <Link key={tag.id || tag.name} to={`/blog/tag/${tag.slug || tag.name?.toLowerCase().replace(/\s+/g, '-')}`}
                            className="px-3 py-1.5 text-xs font-medium rounded-full bg-light text-body hover:bg-primary hover:text-white transition-all duration-300 border border-gray-200 hover:border-primary">
                            {tag.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="py-14 md:py-20 bg-white border-t border-gray-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-primary inline-block relative">
                  Related Posts
                  <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-16 h-1 bg-secondary rounded-full"></span>
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {related.slice(0, 3).map((post) => (
                  <Link key={post.id} to={`/blog/${post.slug}`}
                    className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
                    <div className="relative overflow-hidden">
                      {post.image ? (
                        <img src={`${UPLOADS_URL}/${post.image}`} alt={clean(post.title)} className="w-full h-auto object-contain bg-gray-50 transition-transform duration-700 group-hover:scale-105" />
                      ) : (
                        <div className="w-full aspect-[5/3] bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                          <i className="fas fa-newspaper text-4xl text-primary/40"></i>
                        </div>
                      )}
                      {post.category && (
                        <span className="absolute top-4 left-4 bg-secondary/90 text-white px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm">{post.category}</span>
                      )}
                    </div>
                    <div className="p-5">
                      <p className="text-xs text-muted mb-2">{formatDate(post.created_at)}</p>
                      <h3 className="text-base font-bold text-primary leading-snug group-hover:text-secondary transition-colors line-clamp-2">{clean(post.title)}</h3>
                      <p className="text-sm text-body mt-2 line-clamp-2">{post.content?.replace(/<[^>]*>/g, '').substring(0, 120)}...</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default BlogDetail;
